import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Trash2 } from '@/utils/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useChat } from '@/hooks/useChat';

const SUGGESTED_QUESTIONS = [
  'What Nodes are offline?',
  'Summarize fleet health',
  'Which portfolios have issues?',
  'Show flapping Nodes',
];

export function ChatPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const { messages, loading, error, sendMessage, clearChat } = useChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    setInput('');
    sendMessage(trimmed);
  };

  const handleSuggestion = (question: string) => {
    sendMessage(question);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="dls-fixed dls-bottom-6 dls-right-6 dls-z-50 dls-rounded-full dls-bg-neeve-secondary dls-text-white dls-p-4 dls-shadow-40 hover:dls-bg-neeve-primary dls-transition-colors"
        aria-label="Open chat"
      >
        <MessageCircle className="dls-h-6 dls-w-6" />
      </button>
    );
  }

  return (
    <div className="dls-fixed dls-bottom-6 dls-right-6 dls-z-50 dls-w-96 dls-max-h-[600px] dls-flex dls-flex-col dls-bg-background dls-border dls-rounded-xl dls-shadow-2xl">
      {/* Header */}
      <div className="dls-flex dls-items-center dls-justify-between dls-px-4 dls-py-3 dls-border-b dls-bg-neeve-secondary dls-text-white dls-rounded-t-xl">
        <div className="dls-flex dls-items-center dls-gap-2">
          <MessageCircle className="dls-h-5 dls-w-5" />
          <span className="dls-font-semibold">Fleet Assistant</span>
        </div>
        <div className="dls-flex dls-items-center dls-gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="dls-h-7 dls-w-7 dls-text-white hover:dls-bg-white/20"
            onClick={clearChat}
            title="Clear conversation"
          >
            <Trash2 className="dls-h-4 dls-w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="dls-h-7 dls-w-7 dls-text-white hover:dls-bg-white/20"
            onClick={() => setIsOpen(false)}
          >
            <X className="dls-h-4 dls-w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="dls-flex-1 dls-overflow-y-auto dls-p-4 dls-space-y-4 dls-min-h-[300px] dls-max-h-[450px]">
        {messages.length === 0 ? (
          <div className="dls-space-y-3">
            <p className="dls-text-sm dls-text-muted-foreground dls-text-center">
              Ask me about your fleet status, node health, or portfolio metrics.
            </p>
            <div className="dls-space-y-2">
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => handleSuggestion(q)}
                  className="dls-w-full dls-text-left dls-text-sm dls-px-3 dls-py-2 dls-rounded-lg dls-border hover:dls-bg-muted dls-transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div
              key={i}
              className={cn('dls-text-sm', msg.role === 'user' ? 'dls-text-right' : 'dls-text-left')}
            >
              <div
                className={cn(
                  'dls-inline-block dls-max-w-[85%] dls-px-3 dls-py-2 dls-rounded-lg dls-whitespace-pre-wrap',
                  msg.role === 'user' ? 'dls-bg-neeve-secondary dls-text-white' : 'dls-bg-muted'
                )}
              >
                {msg.content}
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className="dls-text-left">
            <div className="dls-inline-block dls-px-3 dls-py-2 dls-rounded-lg dls-bg-muted dls-text-sm">
              <span className="dls-animate-pulse">Thinking...</span>
            </div>
          </div>
        )}
        {error && (
          <div className="dls-text-center">
            <p className="dls-text-sm dls-text-status-offline">{error}</p>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="dls-border-t dls-p-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="dls-flex dls-gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your fleet..."
            disabled={loading}
            className="dls-flex-1"
          />
          <Button
            type="submit"
            size="icon"
            disabled={!input.trim() || loading}
            className="dls-bg-neeve-secondary hover:dls-bg-neeve-primary"
          >
            <Send className="dls-h-4 dls-w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
